import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  theme: 'noor' | 'sukoon';
  toggleTheme: () => void;
}

const Header: React.FC<HeaderProps> = ({ theme, toggleTheme }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('English');
  const [googleTranslateLoaded, setGoogleTranslateLoaded] = useState(false);
  const location = useLocation();

  const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'te', name: 'Telugu', native: 'తెలుగు' },
    { code: 'ur', name: 'Urdu', native: 'اردو' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
    { code: 'tr', name: 'Turkish', native: 'Türkçe' },
    { code: 'ru', name: 'Russian', native: 'Русский' },
    { code: 'ja', name: 'Japanese', native: '日本語' }
  ];

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Learn Prayer', path: '/learn-prayer' },
    { name: 'Dua & Dhikr', path: '/dua-dhikr' },
    { name: 'Quran', path: '/quran' },
    { name: 'My Mood', path: '/my-mood' },
    { name: 'Prophets', path: '/prophets' },
    { name: 'About Me', path: '/about' }
  ];

  const isActive = (path: string) => location.pathname === path;

  // Initialize Google Translate
  useEffect(() => {
    const initializeGoogleTranslate = () => {
      if ((window as any).google && (window as any).google.translate) {
        new (window as any).google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: 'en,hi,te,ur,ar,tr,ru,ja',
          layout: (window as any).google.translate.TranslateElement.InlineLayout.HORIZONTAL,
          autoDisplay: false
        }, 'google_translate_element');
        
        setGoogleTranslateLoaded(true);
        
        // Hide the Google Translate UI elements we don't want
        setTimeout(() => {
          const googleBanner = document.querySelector('.goog-te-banner-frame');
          const googleMenu = document.querySelector('.goog-te-menu-frame');
          if (googleBanner) (googleBanner as HTMLElement).style.display = 'none';
          if (googleMenu) (googleMenu as HTMLElement).style.display = 'none';
        }, 1000);
      }
    };

    // Load Google Translate script only if not already loaded
    if (!(window as any).google) {
      const script = document.createElement('script');
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.head.appendChild(script);

      (window as any).googleTranslateElementInit = initializeGoogleTranslate;
    } else {
      initializeGoogleTranslate();
    }

    // Cleanup
    return () => {
      const googleBanner = document.querySelector('.goog-te-banner-frame');
      if (googleBanner) (googleBanner as HTMLElement).style.display = 'none';
    };
  }, []);

  const handleLanguageChange = (language: { code: string; name: string; native: string }) => {
    setCurrentLanguage(language.name);
    setLanguageDropdownOpen(false);
    
    // Wait for Google Translate to be fully loaded
    if (googleTranslateLoaded) {
      setTimeout(() => {
        const googleSelect = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (googleSelect && googleSelect.value !== language.code) {
          googleSelect.value = language.code;
          googleSelect.dispatchEvent(new Event('change'));
          
          // Force page refresh to ensure complete translation
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }, 100);
    }
  };

  // SVG Icons
  const InstagramIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  );

  const YouTubeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.94 2c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
      <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/>
    </svg>
  );

  const LanguageIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );

  return (
    <header style={{
      backgroundColor: 'var(--bg-primary)',
      borderBottom: `1px solid var(--border)`,
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backdropFilter: 'blur(10px)'
    }}>
      <div className="container">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '80px',
          padding: '0 16px',
          position: 'relative'
        }}>
          {/* Logo */}
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            gap: '12px'
          }}>
            <div style={{
              width: '44px',
              height: '44px',
              backgroundColor: 'var(--accent-primary)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              color: 'var(--bg-primary)',
              fontWeight: 'bold',
              fontFamily: 'cursive',
              transform: 'rotate(-5deg)'
            }}>
              إ
            </div>
            <div>
              <div className="heading-font" style={{
                fontSize: '26px',
                fontWeight: '700',
                color: 'var(--text-primary)',
                lineHeight: '1',
                fontFamily: 'cursive'
              }}>
                ImanVerse
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                letterSpacing: '0.5px'
              }}>
                Spiritual Guidance
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            margin: '0 32px'
          }}>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                style={{ 
                  margin: '0 2px',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right Section - Language + Social + Theme */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Language Selector */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  border: `1px solid var(--border)`,
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--shadow)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <LanguageIcon />
                <span>{currentLanguage}</span>
                <span style={{ 
                  transform: languageDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  fontSize: '12px'
                }}>
                  ▼
                </span>
              </button>

              {/* Language Dropdown */}
              {languageDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  backgroundColor: 'var(--bg-primary)',
                  border: `1px solid var(--border)`,
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                  minWidth: '180px',
                  zIndex: 1001,
                  overflow: 'hidden'
                }}>
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => handleLanguageChange(language)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: currentLanguage === language.name ? 'var(--accent-primary)' : 'transparent',
                        border: 'none',
                        color: currentLanguage === language.name ? 'var(--bg-primary)' : 'var(--text-primary)',
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentLanguage !== language.name) {
                          e.currentTarget.style.backgroundColor = 'var(--shadow)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentLanguage !== language.name) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span>{language.native}</span>
                      <span style={{ 
                        fontSize: '12px', 
                        opacity: 0.7,
                        color: currentLanguage === language.name ? 'var(--bg-primary)' : 'var(--text-secondary)'
                      }}>
                        {language.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Hidden Google Translate Element */}
            <div 
              id="google_translate_element" 
              style={{ 
                display: 'none',
                position: 'absolute'
              }} 
            />

            {/* Social Icons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <a
                href="https://instagram.com/shahsjourney"
                aria-label="Instagram"
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--shadow)';
                  e.currentTarget.style.color = 'var(--accent-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <InstagramIcon />
              </a>
              <a
                href="https://www.youtube.com/@_shafee_05_"
                aria-label="YouTube"
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--shadow)';
                  e.currentTarget.style.color = 'var(--accent-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <YouTubeIcon />
              </a>
            </div>

            {/* Theme Toggle */}
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'noor' ? 'dark' : 'light'} theme`}
              style={{
                width: '44px',
                height: '44px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {theme === 'noor' ? '🌙' : '☀️'}
            </button>

            {/* Mobile Menu Button */}
            <button 
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '10px',
                borderRadius: '8px',
                display: 'none',
                color: 'var(--text-secondary)'
              }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--bg-primary)',
            borderBottom: `1px solid var(--border)`,
            padding: '20px 0',
            zIndex: 1001
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              padding: '0 24px'
            }}>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ 
                    display: 'block', 
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Language Selector for Mobile */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', marginTop: '8px' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Select Language:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => {
                        handleLanguageChange(language);
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: currentLanguage === language.name ? 'var(--accent-primary)' : 'var(--shadow)',
                        border: 'none',
                        borderRadius: '6px',
                        color: currentLanguage === language.name ? 'var(--bg-primary)' : 'var(--text-primary)',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {language.code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <Link
                to="/donate"
                className="nav-link"
                onClick={() => setMobileMenuOpen(false)}
                style={{ 
                  display: 'block', 
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
              >
                Donate
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdowns */}
      {(languageDropdownOpen || mobileMenuOpen) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => {
            setLanguageDropdownOpen(false);
            setMobileMenuOpen(false);
          }}
        />
      )}

      {/* Hide Google Translate UI elements */}
      <style>{`
        .goog-te-banner-frame {
          display: none !important;
        }
        .goog-te-menu-frame {
          display: none !important;
        }
        .goog-te-menu-value {
          display: none !important;
        }
        .goog-te-gadget {
          font-size: 0 !important;
        }
        .goog-te-combo {
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
        }
        .skiptranslate {
          display: none !important;
        }
        
        @media (max-width: 1024px) {
          nav { display: none !important; }
          button[aria-label="Toggle menu"] { display: block !important; }
        }
        
        @media (max-width: 768px) {
          header .container > div { 
            height: 70px; 
            padding: 0 12px;
          }
          .language-selector span:last-child {
            display: none;
          }
        }
        
        @media (max-width: 480px) {
          .container { 
            padding: 0 12px; 
          }
          header .container > div { 
            padding: 0 8px;
          }
          .language-selector {
            padding: 6px 8px !important;
          }
          .language-selector span {
            display: none;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;